import classNames from "classnames";
import { find, get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdCheck,
  MdCheckCircle,
  MdClose,
  MdLocalHospital,
  MdLock,
  MdOutlineAccountBalanceWallet,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineDelete,
  MdOutlinePeople,
  MdOutlinePerson,
  MdSave,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  RELATIONSHIP,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";

import cuid from "cuid";
import GeneralCosts from "entities/GeneralCosts";
import { FieldArray, Formik } from "formik";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import EmptyState from "shared/components/EmptyState";
import Loading from "shared/components/Spin";
import { COLORS } from "shared/style/colors";
import format from "string-template";
import {
  bindDateInputProps,
  bindInputProps,
  bindTextFieldInputProps,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import InfoBox from "../Components/InfoBox";
import validation from "./validation";

const RELATIONSHIPS = [
  { value: RELATIONSHIP.SPOUSE, label: "Conjoint(e)" },
  { value: RELATIONSHIP.CHILD, label: "Enfant" },
];

const CASHBACK_PLANS = [
  {
    id: "sans",
    cost: null,
    priceLabel: "0",
    insurancePlan: {
      id: "sans",
      name: "Sans",
      description: "Pas d'accès au programme cashback",
    },
  },
];

const HINT = "Ajoutez {count} membre au foyer pour accéder à cette formule";

const RELATIONSHIP_LABEL = {
  [RELATIONSHIP.SPOUSE]: "Conjoint(e)",
  [RELATIONSHIP.CHILD]: "Enfant",
};

const householdSizeFromProject = (project) =>
  1 + (get(project, "contact.relatedPersons", []) || []).length;

const isPlanLocked = (insurancePlan, householdSize) => {
  if (get(insurancePlan, "alias") === "duo") return householdSize < 2;
  if (get(insurancePlan, "alias") === "famille") return householdSize < 3;
  return false;
};

const PLAN_COUNT = {
  solo: 1,
  duo: 2,
  famille: 3,
};

const PlanOption = ({
  generalCost,
  selected,
  locked,
  variant,
  onSelect,
  householdSize,
}) => {
  const planCount = PLAN_COUNT[get(generalCost, "insurancePlan.alias")];
  const supportingInformation = get(
    find(get(generalCost, "insurancePlan.supportingInformation", []), {
      category: SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
    }),
    "value",
    [],
  );
  const handleClick = () => {
    if (locked) return;
    onSelect(generalCost);
  };

  const handleKeyDown = (e) => {
    if (locked) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(generalCost);
    }
  };

  const priceText =
    get(generalCost, "cost") === null
      ? 0
      : get(generalCost, "cost") === 0
        ? "Gratuite"
        : `${get(generalCost, "cost", 0).toFixed(2)} €`;
  const showPeriod = !!get(generalCost, "cost");

  const priceClass = classNames("lf-select-products__plan-price", {
    "lf-select-products__plan-price--tele": variant === "SERVICE" && !locked,
    "lf-select-products__plan-price--tele-muted":
      variant === "SERVICE" && locked,
    "lf-select-products__plan-price--cashback":
      variant === "CASHBACK" && get(generalCost, "insurancePlan.id") !== "sans",
    "lf-select-products__plan-price--cashback-neutral":
      variant === "CASHBACK" && get(generalCost, "insurancePlan.id") === "sans",
    "lf-select-products__plan-price--cashback-muted":
      variant === "CASHBACK" && locked,
  });

  const featureIconClass = classNames("lf-select-products__feature-icon", {
    "lf-select-products__feature-icon--tele": variant === "SERVICE" && !locked,
    "lf-select-products__feature-icon--cashback":
      variant === "CASHBACK" && !locked,
    "lf-select-products__feature-icon--muted": locked,
  });

  return (
    <div
      className={classNames(
        "lf-select-products__plan",
        variant === "SERVICE"
          ? "lf-select-products__plan--tele"
          : "lf-select-products__plan--cashback",
        {
          "lf-select-products__plan--selected": selected && !locked,
          "lf-select-products__plan--locked": locked,
        },
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={locked ? -1 : 0}
    >
      <div className="lf-select-products__plan-corner">
        {locked ? (
          <MdLock className="lf-select-products__plan-corner--lock" />
        ) : null}
        {!locked && selected ? (
          <MdCheckCircle
            className={classNames({
              "lf-select-products__plan-corner--check-tele":
                variant === "SERVICE",
              "lf-select-products__plan-corner--check-cashback":
                variant === "CASHBACK",
            })}
          />
        ) : null}
      </div>

      <h3 className="lf-select-products__plan-title">
        {get(generalCost, "insurancePlan.name")}
      </h3>
      <p className={priceClass}>{priceText}</p>
      {showPeriod ? (
        <p className="lf-select-products__plan-period">par mois</p>
      ) : (
        <p className="lf-select-products__plan-period">&nbsp;</p>
      )}
      <p className="lf-select-products__plan-desc">
        {get(generalCost, "insurancePlan.description")}
      </p>

      {supportingInformation && supportingInformation.length > 0 ? (
        <ul className="lf-select-products__plan-features">
          {supportingInformation.map((supportingInfo) => (
            <li
              key={get(supportingInfo, "mainInfo")}
              className="lf-select-products__plan-feature"
            >
              <MdCheck className={featureIconClass} />
              <span>{get(supportingInfo, "mainInfo")}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {locked && planCount ? (
        <div className="lf-select-products__plan-hint">
          {format(HINT, { count: planCount - householdSize })}
        </div>
      ) : null}
    </div>
  );
};

const View = ({
  filter,
  upSellsFilter,
  take,
  onSubmit,
  nextLoading,
  project,
  onBack,
  selectedInsurancePlan,
  setSelectedInsurancePlan,
  selectedCashback,
  setSelectedCashback,
  selectedProducts,
  loading,
  upSells,
  handleRelatedPersonSave,
}) => {
  const [relatedPersonsEdit, setRelatedPersonsEdit] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });

  const householdSize = householdSizeFromProject(project);
  const relatedPersons = get(project, "contact.relatedPersons", []) || [];

  const holderFirst = get(project, "contact.user.firstname", "");
  const holderLast = get(project, "contact.user.lastname", "");
  const holderDisplay = (
    <>
      <p>
        <strong>{`${holderFirst} ${holderLast}`.trim()}</strong>
      </p>
      <p>Titulaire du contrat</p>
    </>
  );

  const onRelatedPersonsSubmit = (values) => {
    handleRelatedPersonSave(values, () => setRelatedPersonsEdit(false));
  };

  return (
    <div className="lf-select-products template-product-list--container">
      <div className="template-product-list--wrapper">
        <section className="lf-select-products__section">
          <div className="lf-select-products__section-head">
            <div
              className="lf-select-products__section-icon lf-select-products__section-icon--tele"
              aria-hidden
            >
              <MdLocalHospital />
            </div>
            <div className="lf-select-products__section-titles">
              <h2 className="lf-select-products__section-title">
                Téléconsultation Médicale
              </h2>
              <p className="lf-select-products__section-sub">
                Formule adaptée à la composition du foyer
              </p>
            </div>
          </div>

          <div className="lf-select-products__grid">
            {loading ? (
              <Loading />
            ) : (
              <GeneralCosts
                fetchPolicy={"cache-and-network"}
                filter={filter}
                take={take}
                View={({ data }) => {
                  return data.map((generalCost) => {
                    const locked = isPlanLocked(
                      get(generalCost, "insurancePlan"),
                      householdSize,
                    );
                    const selected =
                      get(selectedInsurancePlan, "insurancePlan.id") ===
                      get(generalCost, "insurancePlan.id");
                    return (
                      <PlanOption
                        householdSize={householdSize}
                        key={get(generalCost, "insurancePlan.id")}
                        generalCost={omitDeep(generalCost, "__typename")}
                        selected={selected}
                        locked={locked}
                        variant="SERVICE"
                        onSelect={setSelectedInsurancePlan}
                      />
                    );
                  });
                }}
                NoData={() => (
                  <EmptyState
                    title={DETAIL.TEMPLATES.PRODUCTS.NO_PRODUCTS}
                    subtitle={DETAIL.TEMPLATES.PRODUCTS.NO_PRODUCTS_DESCRIPTION}
                  />
                )}
              />
            )}
          </div>

          <div className="lf-select-products__household">
            <div className="lf-select-products__household-top">
              <div className="lf-select-products__household-head">
                <div className="lf-select-products__household-icon" aria-hidden>
                  <MdOutlinePeople />
                </div>
                <h3 className="lf-select-products__household-title">
                  Composition du foyer
                </h3>
              </div>
              {!relatedPersonsEdit && (
                <Button
                  type="primary--outlined"
                  onClick={() => setRelatedPersonsEdit(true)}
                >
                  Modifier le foyer
                </Button>
              )}
            </div>
            <div className="lf-select-products__household-list-wrap">
              <InfoBox variant="info--outline" content={holderDisplay} />
              {relatedPersons.map((p) => {
                const fn = get(
                  p,
                  "identity.user.firstname",
                  get(p, "firstName", ""),
                );
                const ln = get(
                  p,
                  "identity.user.lastname",
                  get(p, "lastName", ""),
                );
                const rel = get(p, "relationship", "");
                const relLabel = RELATIONSHIP_LABEL[rel] || "Membre du foyer";
                const name = `${fn} ${ln}`.trim();
                if (!name) return null;
                return (
                  <InfoBox
                    key={get(p, "id", `${fn}-${ln}-${rel}`)}
                    variant="info--outline"
                    content={
                      <>
                        <p>{name}</p>
                        {relLabel}
                      </>
                    }
                  />
                );
              })}
            </div>
            {relatedPersonsEdit && (
              <InfoBox
                variant="info--outline"
                content={
                  <>
                    <Formik
                      validateOnMount={true}
                      initialValues={{
                        members: (
                          get(project, "contact.relatedPersons", []) || []
                        ).map((p) => ({
                          id: p.id || cuid(),
                          firstName: get(p, "identity.user.firstname", ""),
                          lastName: get(p, "identity.user.lastname", ""),
                          birthDate: get(p, "identity.user.birthDate", null),
                          relationship: get(p, "relationship", ""),
                        })),
                      }}
                      onSubmit={onRelatedPersonsSubmit}
                      validationSchema={validation(
                        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
                          returnObjects: true,
                        }),
                      )}
                    >
                      {({
                        values,
                        handleSubmit,
                        setFieldValue,
                        isValid,
                        ...formProps
                      }) => {
                        return (
                          <div className="lf-select-products__related-persons-form">
                            <FieldArray
                              name="members"
                              render={(arrayHelpers) => (
                                <>
                                  {values.members.map((member, i) => (
                                    <InfoBox
                                      key={`member-${member.id}`}
                                      variant="info--outline"
                                      content={
                                        <div>
                                          <div className="contact-stepper__content--box--header">
                                            <h1 className="contact-stepper__content--box--header--title">
                                              Membre {i + 1}
                                            </h1>
                                            <div
                                              className="contact-stepper__content--box--header--action"
                                              onClick={() =>
                                                arrayHelpers.remove(i)
                                              }
                                            >
                                              <MdOutlineDelete
                                                color={COLORS.C_RED}
                                                size={18}
                                              />
                                            </div>
                                          </div>
                                          <div className="contact-stepper__content--box--content">
                                            <Input
                                              icon={
                                                <MdOutlineBadge size={22} />
                                              }
                                              label="Prénom"
                                              placeholder="Prénom"
                                              {...bindTextFieldInputProps({
                                                name: `members.${i}.firstName`,
                                                values,
                                                setFieldValue,
                                                ...formProps,
                                              })}
                                            />
                                            <Input
                                              icon={
                                                <MdOutlineBadge size={22} />
                                              }
                                              label="Nom"
                                              placeholder="Nom"
                                              {...bindTextFieldInputProps({
                                                name: `members.${i}.lastName`,
                                                values,
                                                setFieldValue,
                                                ...formProps,
                                              })}
                                            />
                                            <Input
                                              icon={<MdOutlineCake size={22} />}
                                              label="Date de naissance"
                                              type="date"
                                              variant="simple"
                                              format={DATE_FORMAT}
                                              {...bindDateInputProps({
                                                name: `members.${i}.birthDate`,
                                                values,
                                                setFieldValue,
                                                ...formProps,
                                              })}
                                            />
                                            <Select
                                              icon={
                                                <MdOutlinePerson size={22} />
                                              }
                                              label="Lien de parenté"
                                              placeholder="Sélectionner"
                                              options={RELATIONSHIPS}
                                              {...bindInputProps({
                                                name: `members.${i}.relationship`,
                                                values,
                                                ...formProps,
                                              })}
                                            />
                                          </div>
                                        </div>
                                      }
                                    />
                                  ))}
                                  <Button
                                    key="add-member"
                                    type="primary--outlined"
                                    className="lf-select-products__related-persons-form--button-add"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        id: cuid(),
                                        firstName: "",
                                        lastName: "",
                                        birthDate: null,
                                        relationship: "",
                                      })
                                    }
                                  >
                                    <MdAdd size={22} />
                                    Ajouter un membre
                                  </Button>
                                  <div className="lf-select-products__related-persons-form--buttons">
                                    <Button
                                      type="primary--outlined"
                                      onClick={() =>
                                        setRelatedPersonsEdit(false)
                                      }
                                    >
                                      <MdClose size={18} />
                                      Annuler
                                    </Button>
                                    <Button
                                      type="primary"
                                      onClick={handleSubmit}
                                      disabled={!isValid}
                                    >
                                      <MdSave size={18} />
                                      Enregistrer les modifications
                                    </Button>
                                  </div>
                                </>
                              )}
                            />
                          </div>
                        );
                      }}
                    </Formik>
                  </>
                }
              />
            )}
            {!relatedPersonsEdit && relatedPersons.length === 0 ? (
              <InfoBox
                variant9="info--outline"
                content={
                  <div className="lf-select-products__household-info">
                    <p>Aucun autre membre dans le foyer pour le moment.</p>
                    <p>
                      Cliquez sur "Modifier le foyer" pour ajouter des membres
                    </p>
                  </div>
                }
              />
            ) : null}
          </div>
        </section>

        <section className="lf-select-products__section">
          <div className="lf-select-products__section-head">
            <div
              className="lf-select-products__section-icon lf-select-products__section-icon--cashback"
              aria-hidden
            >
              <MdOutlineAccountBalanceWallet />
            </div>
            <div className="lf-select-products__section-titles">
              <h2 className="lf-select-products__section-title">
                Programme Cashback La Fourgale
              </h2>
              <p className="lf-select-products__section-sub">
                Option facultative
              </p>
            </div>
          </div>
          <GeneralCosts
            fetchPolicy={"cache-and-network"}
            filter={upSellsFilter}
            take={take}
            View={({ data }) => {
              return (
                <>
                  <div className="lf-select-products__grid lf-select-products__grid--4">
                    {CASHBACK_PLANS.concat(data).map((generalCost) => {
                      const selected =
                        get(selectedCashback, "insurancePlan.id") ===
                        get(generalCost, "insurancePlan.id");
                      return (
                        <PlanOption
                          key={generalCost.id}
                          generalCost={omitDeep(generalCost, "__typename")}
                          selected={selected}
                          locked={false}
                          variant="CASHBACK"
                          onSelect={setSelectedCashback}
                          householdSize={householdSize}
                        />
                      );
                    })}
                  </div>
                </>
              );
            }}
          />

          <div className="quote--recommendation">
            <span className="quote--recommendation--title">
              Votre téléconsultation privée est financée par vos réductions dans
              plus de 3.000 Marques et Enseignes
            </span>
            <ul>
              <li className="quote--recommendation--description">
                À tout moment en moyenne 19% de réductions et Cashbacks
              </li>
              <li className="quote--recommendation--description">
                Auprès de plus de 3000 Enseignes et Marques présentes dans les
                centres commerciaux et les plus grands sites d’e-commerce
              </li>
              <li className="quote--recommendation--description">
                Entre 3% et 9% de réduction sur votre Caddy de supermarché
              </li>
              <li className="quote--recommendation--description">
                Entre 5 et 15% de réduction sur votre facture Energie et Mobile
              </li>
              <li className="quote--recommendation--description">
                Entre 5 et 10 % de réduction sur vos cotisations Mutuelle et
                assurance
              </li>
            </ul>
          </div>
        </section>

        <div className="lf-select-products__total">
          <div>
            <p className="lf-select-products__total-label">
              Montant total mensuel
            </p>
            <p className="lf-select-products__total-hint">
              Calcul en temps réel
            </p>
          </div>
          <p className="lf-select-products__total-price">
            {(
              get(selectedInsurancePlan, "cost", 0) +
              get(selectedCashback, "cost", 0)
            ).toFixed(2)}{" "}
            €<small>/mois</small>
          </p>
        </div>
        <ActionButtons
          onBack={onBack}
          onSubmit={onSubmit}
          loading={nextLoading}
          disabled={{
            next:
              !selectedInsurancePlan?.insurancePlan?.id ||
              !selectedCashback?.insurancePlan?.id,
          }}
          translation={DETAIL}
        />
      </div>
    </div>
  );
};

export default View;
